// M3 environmental & safety regimes (M3_SPEC §13-§14): what a spill costs by era (common law → NEPA → CWA → OCSLAA →
// OPA 90), OPA 90's spill-response requirement, regulators' inspections (OSHA 1971, OCS Orders, MMS, UK MWOIA, NPD,
// post-Kielland, Ocean Ranger), the UK Safety Case (31 May 1993; existing installations by Nov 1995), offshore insurance
// that spikes after disasters and decays (Piper Alpha ×2 for 5 years), and platform fires on manned installations.
import { wellsOnField, allWells } from '../indexes.js';
import { spillRegime, SPILL_REMOTE_MULT, SPILL_INTERNATIONAL, SAFETY_REGIMES, safetyRegimesAt, OFFSHORE_INSURANCE, BASE_OFFSHORE_INSURANCE_RATE, M3_FROM } from '../../data/m3/rules.js';
import { chance, range, int } from '../../core/rng.js';
import { dateToDay } from '../../core/time.js';
import { eqDef, fieldDef, regionDef, unitPrice, eraDollars, stat, yf } from '../defs.js';
import { effects } from '../research.js';
import { notify, spend, fmt$, clamp } from '../util.js';
import { econState, bumpStat, m3On } from './econ.js';
import { isUsRegion } from './prices.js';
import { m4IncidentMult } from '../m4/worldHooks.js'; // M4-C

export const OPA_REGIONS = ['gulf_of_mexico', 'alaska', 'california'];
export const OPA_FROM = 1990.63;
export const SPILL_REP_CAP = 40;
export const FLOWLINE_SPILL_PER_WELL_WEEK = 0.002;
export const PLATFORM_FIRE_PER_YEAR = 0.004;          // base, × def fireRiskMult (Piper: compression next to the control room)
export const PUNITIVE_MIN_BBL = 100000;
export const CITATION_CHANCE = 0.25;                   // an inspection finds a serious violation anyway
export const WILLFUL_SHUTDOWN_DAYS = 30;
const SAFETY_CASE_DAY = dateToDay(1993, 5, 31);

const hasRole = (state, role) => Object.values(state.crew).some((c) => c.role === role && c.status === 'active');
const platformsAt = (state, fid) => Object.values(state.platforms ?? {}).filter((p) => p.fieldId === fid);
const rigAt = (r) => r.location?.fieldId ?? r.at?.fieldId ?? null;

// Is safety/environment gear `defId` fitted for field fid (rig upgrades, platform upgrades, items on site or in stock)?
export function fittedAt(state, defId, fid = null) {
  if (state.safety?.crewGear?.[defId]) return true;
  const items = state.items;
  for (const k in items) { // perf: for-in (no array per call; same order as Object.values) — hot via platformFireChance
    const i = items[k];
    if (i.defId !== defId) continue;
    const l = i.loc ?? {};
    if (!fid || l.type === 'warehouse' || l.id === fid || state.platforms?.[l.id]?.fieldId === fid || rigAt(state.rigs[l.id] ?? {}) === fid) return true;
  }
  for (const p of fid ? platformsAt(state, fid) : Object.values(state.platforms ?? {})) if ((p.upgrades ?? []).includes(defId)) return true;
  for (const r of Object.values(state.rigs)) if ((r.safety?.upgrades ?? []).includes(defId) && (!fid || !rigAt(r) || rigAt(r) === fid)) return true;
  return false;
}
const ownsOsro = (state) => fittedAt(state, 'spill_response_contract');

// ---- spills -------------------------------------------------------------------------------------------
export function spillQuote(state, fieldId, bbl) {
  const y = yf(state);
  const reg = spillRegime(y);
  const rid = fieldDef(fieldId)?.regionId ?? null;
  const intl = !isUsRegion(rid);
  const remote = SPILL_REMOTE_MULT[rid] ?? SPILL_REMOTE_MULT.default;
  let keep = 1;
  if (fittedAt(state, 'containment_boom', fieldId)) keep *= 1 - 0.3;
  if (ownsOsro(state)) keep *= 1 - 0.5;
  const $ = (x) => eraDollars(state, x, reg.baseYear ?? 1970);
  let cleanup = $(reg.cleanupPerBbl) * bbl * remote * (intl ? SPILL_INTERNATIONAL.cleanupMult : 1) * keep;
  if (!reg.removalUnlimited && Number.isFinite(reg.liabilityCap)) cleanup = Math.min(cleanup, $(reg.liabilityCap));
  let fine = $(reg.finePerBbl ?? 0) * bbl * (intl ? SPILL_INTERNATIONAL.fineMult : 1);
  if (hasRole(state, 'counsel')) fine *= 0.75;
  const rep = -Math.min(SPILL_REP_CAP, Math.abs(reg.repPerKbbl) * 10 * Math.log10(1 + bbl / 1000) * (intl ? SPILL_INTERNATIONAL.repMult : 1));
  const punitive = bbl > PUNITIVE_MIN_BBL ? (reg.punitivePerBbl != null ? $(reg.punitivePerBbl) : $(reg.finePerBbl ?? 0) * 5) * bbl * (intl ? SPILL_INTERNATIONAL.fineMult : 1) : 0;
  return { regime: reg.label, cleanup, fine, rep, punitive, punitiveChance: bbl > PUNITIVE_MIN_BBL ? reg.punitiveChance ?? 0 : 0, international: intl, remote };
}

// Charge a spill of `bbl` at a field (any source: blowouts at sea, flowlines, tankers, storm damage, tank overflow).
export function chargeSpill(state, fieldId, bbl, cause = 'spill', { pause = null, wellId = null, silent = false } = {}) {
  if (!(bbl > 0) || !m3On(state)) return null;
  markSpill(state, fieldId, bbl, cause, { wellId, ours: true });
  const q = spillQuote(state, fieldId, bbl);
  const pun = q.punitiveChance > 0 && chance(state, q.punitiveChance) ? q.punitive : 0;
  spend(state, q.cleanup, 'spills', false);
  spend(state, q.fine + pun, 'fines', false);
  state.company.reputation = clamp(state.company.reputation + q.rep, 0, 100);
  bumpStat(state, 'bblSpilled', bbl);
  const cost = q.cleanup + q.fine + pun;
  bumpStat(state, 'spillCosts', cost);
  const big = bbl >= 10000;
  if (silent) return { ...q, punitive: pun, cost };
  notify(state, 'spill', {
    fieldId, bbl: Math.round(bbl), cost, fine: q.fine + pun, rep: q.rep, cause, regime: q.regime, pause: pause ?? big, level: big ? 'major' : 'warn',
    text: `${Math.round(bbl).toLocaleString('en-US')} bbl spilled at ${fieldDef(fieldId)?.name ?? fieldId} (${cause}): cleanup ${fmt$(q.cleanup)}, fines ${fmt$(q.fine + pun)}${pun ? ' incl. punitive damages' : ''}. Reputation ${Math.round(q.rep)}.`,
  });
  return { ...q, punitive: pun, cost };
}

// Spill records for the renderers: fieldState.spill / well.spill = { bbl, day, cleanup (0 → 1 done), cause, ours, cleanupDays }.
export const CLEANUP_DAYS = { base: 20, perKbbl: 1.5, max: 400 };
export function markSpill(state, fieldId, bbl, cause, { wellId = null, ours = true } = {}) {
  const f = state.fields[fieldId];
  if (!f || !(bbl > 0)) return null;
  const prev = f.spill && f.spill.cleanup < 1 ? f.spill : null;
  const total = (prev?.bbl ?? 0) + bbl;
  const rec = { bbl: Math.round(total), day: prev?.day ?? state.day, lastDay: state.day, cleanup: prev ? Math.min(prev.cleanup, 0.9) : 0, cause, ours: ours || !!prev?.ours,
    cleanupDays: Math.round(Math.min(CLEANUP_DAYS.max, CLEANUP_DAYS.base + CLEANUP_DAYS.perKbbl * total / 1000)) };
  f.spill = rec;
  const w = wellId ? state.wells[wellId] : null;
  if (w) w.spill = { bbl: Math.round((w.spill?.cleanup < 1 ? w.spill.bbl : 0) + bbl), day: w.spill?.cleanup < 1 ? w.spill.day : state.day, cleanup: 0, cause };
  return rec;
}
// Historical spills that are not the player's (rendered, not charged): news id → field and size.
export const SCRIPTED_SPILLS = { ixtoc: { fieldId: 'cantarell', bbl: 3.3e6 }, exxon_valdez: { fieldId: 'prudhoe_bay', bbl: 260000, at: 'valdez' } };
export function scriptedSpill(state, newsId) {
  const sp = SCRIPTED_SPILLS[newsId];
  if (!sp || !state.fields[sp.fieldId]) return null;
  return markSpill(state, sp.fieldId, sp.bbl, newsId, { ours: false });
}
function tickSpillCleanup(state) {
  const boost = (fid) => (fittedAt(state, 'containment_boom', fid) ? 1.3 : 1) * (ownsOsro(state) ? 1.5 : 1);
  for (const f of Object.values(state.fields)) {
    const sp = f.spill;
    if (!sp) continue;
    if (sp.lastDay === state.day) continue; // still flowing today
    sp.cleanup = Math.min(1, sp.cleanup + boost(f.id) / Math.max(1, sp.cleanupDays));
    if (sp.cleanup >= 1 && state.day - (sp.lastDay ?? sp.day) > sp.cleanupDays + 30) f.spill = null;
  }
  for (const w of allWells(state)) {
    if (!w.spill) continue;
    const fs = state.fields[w.fieldId]?.spill;
    w.spill.cleanup = fs ? fs.cleanup : 1;
    if (!fs) w.spill = null;
  }
}
// Offshore wells flowing wild (gushers/blowouts at sea): what is not caught goes into the sea, charged daily.
function tickOffshoreBlowouts(state) {
  const E = econState(state);
  E.wildLost ??= {};
  for (const w of allWells(state)) {
    if (!w.offshore || !w.gusher || w.ownerOperatorId) { if (E.wildLost[w.id] != null) delete E.wildLost[w.id]; continue; }
    const lost = w.gusher.lost ?? 0;
    const prev = E.wildLost[w.id];
    E.wildLost[w.id] = lost;
    const d = prev == null ? 0 : lost - prev;
    if (d > 0.5) chargeSpill(state, w.fieldId, d, 'blowout at sea', { wellId: w.id, silent: prev != null && state.fields[w.fieldId]?.spill?.cause === 'blowout at sea' });
  }
}

// ---- insurance ------------------------------------------------------------------------------------------
export function offshoreInsuranceMult(y) {
  let m = 1;
  for (const e of OFFSHORE_INSURANCE) if (y >= e.from) m *= 1 + (e.mult - 1) * Math.max(0, 1 - (y - e.from) / e.years);
  return m;
}
export function offshoreInsuredValue(state) {
  let v = 0;
  for (const r of Object.values(state.rigs)) if (r.offshore ?? stat(eqDef(r.defId), 'offshore', false)) v += unitPrice(state, eqDef(r.defId)) * (r.condition ?? 100) / 100;
  for (const p of Object.values(state.platforms ?? {})) if (p.status !== 'BUILDING') v += p.price ?? unitPrice(state, eqDef(p.defId));
  return v;
}
function monthlyInsurance(state) {
  const v = offshoreInsuredValue(state);
  if (!(v > 0)) return 0;
  const prem = v * BASE_OFFSHORE_INSURANCE_RATE * offshoreInsuranceMult(yf(state)) / 12;
  spend(state, prem, 'insurance', false);
  return prem;
}

// ---- incidents & inspections -------------------------------------------------------------------------
// Multiplier on incident chances from the regimes in force (for safety.incidentChance / the ops agent's incidents).
export function regimeIncidentMult(state, regionId, { offshore = false } = {}) {
  let m = 1;
  for (const r of safetyRegimesAt(regionId, yf(state))) if (!r.offshoreOnly || offshore) m *= r.incidentMult ?? 1;
  return m * m4IncidentMult(state, regionId); // M4-C: SEMS in the Gulf from Nov 2011 (1 before 2000)
}

function sitesByRegion(state) {
  const out = {};
  for (const w of allWells(state)) {
    if (w.ownerOperatorId || !['PRODUCING', 'DRILLING', 'RIGGING_UP', 'TRIPPING', 'CASING', 'SHUT_IN'].includes(w.status)) continue;
    const rid = fieldDef(w.fieldId)?.regionId;
    if (rid) (out[rid] ??= []).push(w);
  }
  return out;
}

function inspect(state, rid, reg, wells) {
  const E = econState(state);
  const offshore = wells.some((w) => w.offshore);
  if (reg.offshoreOnly && !offshore) return;
  const fid = wells[0].fieldId;
  const missing = (reg.requires ?? []).filter((d) => eqDef(d) && (eqDef(d).year ?? 0) <= yf(state) && !fittedAt(state, d, fid));
  const serious = missing.length + (chance(state, CITATION_CHANCE) ? 1 : 0);
  if (!serious) return;
  const key = `${rid}:${reg.id}`;
  const repeat = missing.length > 0 && E.inspections[key] != null && state.day - E.inspections[key] < 365;
  E.inspections[key] = state.day;
  const $ = (x) => eraDollars(state, x, reg.baseYear ?? 1971);
  let fine = repeat ? $(reg.finePerWillful) * Math.max(1, missing.length) : $(reg.finePerSerious) * serious;
  if (hasRole(state, 'counsel')) fine *= 0.75;
  spend(state, fine, 'fines', false);
  let shut = '';
  if (repeat) {
    for (const w of wells) if (w.prod) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + WILLFUL_SHUTDOWN_DAYS);
    shut = ` Willful repeat: operations in ${regionDef(rid)?.name} shut down for ${WILLFUL_SHUTDOWN_DAYS} days.`;
  }
  notify(state, 'news', { kind: 'safety', regionId: rid, level: repeat ? 'major' : 'warn', pause: repeat, text: `${reg.label} inspectors cite you: ${missing.length ? `missing ${missing.map((d) => eqDef(d)?.name ?? d).join(', ')}` : 'a serious violation'} — fine ${fmt$(fine)}.${shut}` });
}

// ---- UK safety case -----------------------------------------------------------------------------------
export function safetyCaseStatus(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const reg = SAFETY_REGIMES.find((r) => r.safetyCase && r.regionIds.includes(fdef?.regionId) && yf(state) >= r.from);
  if (!reg) return null;
  const sc = state.m3econ?.safetyCases?.[fieldId] ?? null;
  const existing = wellsOnField(state, fieldId).some((w) => w.prod?.startDay != null && w.prod.startDay < SAFETY_CASE_DAY);
  const deadline = existing ? reg.safetyCase.existingDeadline : reg.from;
  const missing = (reg.requires ?? []).filter((d) => !fittedAt(state, d, fieldId));
  const ready = !!sc && state.day >= sc.readyDay;
  const due = yf(state) >= deadline;
  return { regime: reg.label, existing, deadline, case: sc, ready, missing, due, shutIn: due && (!ready || missing.length > 0), cost: eraDollars(state, reg.safetyCase.cost, reg.baseYear), days: reg.safetyCase.days };
}

export function prepareSafetyCaseImpl(state, fieldId) {
  if (!state.fields[fieldId]) return { ok: false, msg: 'No such field.' };
  const s = safetyCaseStatus(state, fieldId);
  if (!s) return { ok: false, msg: 'No safety-case regime applies here (UK sector from 31 May 1993).' };
  if (s.case) return { ok: false, msg: s.ready ? 'The safety case is accepted.' : 'The safety case is already being written.' };
  if (!spend(state, s.cost, 'licenses')) return { ok: false, msg: `A safety case costs ${fmt$(s.cost)}.` };
  econState(state).safetyCases[fieldId] = { startDay: state.day, readyDay: state.day + s.days };
  return { ok: true, msg: `Consultants start the formal safety assessment for ${fieldDef(fieldId).name}: ready in ${s.days} days.${s.missing.length ? ` The case also needs: ${s.missing.map((d) => eqDef(d)?.name ?? d).join(', ')}.` : ''}` };
}

function enforceShutIns(state) {
  const E = econState(state);
  const y = yf(state);
  const byField = {};
  for (const w of allWells(state)) if (w.status === 'PRODUCING' && w.prod && !w.ownerOperatorId) (byField[w.fieldId] ??= []).push(w);
  for (const [fid, wells] of Object.entries(byField)) {
    const rid = fieldDef(fid)?.regionId;
    let reason = null;
    const sc = safetyCaseStatus(state, fid);
    if (sc?.shutIn) reason = sc.ready ? `UK Safety Case: missing ${sc.missing.map((d) => eqDef(d)?.name ?? d).join(', ')}` : 'UK Safety Case: no accepted safety case';
    if (!reason && y >= OPA_FROM && OPA_REGIONS.includes(rid) && wells.some((w) => w.offshore) && !ownsOsro(state)) reason = 'OPA 90: no oil-spill response organization membership';
    if (!reason) { if (E.shutIns[fid]) delete E.shutIns[fid]; continue; }
    for (const w of wells) if (!sc?.shutIn ? w.offshore : true) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + 1);
    if (E.shutIns[fid] !== reason) {
      E.shutIns[fid] = reason;
      notify(state, 'safetyShutIn', { fieldId: fid, regionId: rid, reason, pause: true, level: 'major', text: `${fieldDef(fid)?.name}: production shut in — ${reason}.` });
    }
  }
}

// ---- platform fires (manned installations) -----------------------------------------------------------
export function platformFireChance(state, p) {
  const def = eqDef(p.defId);
  if (!def || !stat(def, 'manned', true)) return 0;
  let keep = 1;
  for (const d of ['riser_esdv', 'temporary_refuge', 'fire_deluge', 'tempsc_lifeboat']) if (fittedAt(state, d, p.fieldId) || (p.upgrades ?? []).includes(d)) keep *= 1 - (stat(eqDef(d), 'mitigates', {}).platform_fire ?? 0);
  const sc = state.m3econ?.safetyCases?.[p.fieldId];
  const caseMult = (sc && state.day >= sc.readyDay) ? Math.min(effects(state).platformFireMult ?? 1, 0.6) : (effects(state).platformFireMult ?? 1);
  return PLATFORM_FIRE_PER_YEAR / 365 * stat(def, 'fireRiskMult', 1) * keep * caseMult * regimeIncidentMult(state, fieldDef(p.fieldId)?.regionId, { offshore: true });
}

function platformFire(state, p) {
  const def = eqDef(p.defId);
  p.condition = Math.max(0, (p.condition ?? 100) - range(state, 40, 100));
  const down = int(state, 60, 365);
  for (const w of allWells(state)) if (w.platformUid === p.uid && w.prod) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + down);
  const pob = stat(def, 'pob', 100);
  let keep = 1;
  for (const d of ['temporary_refuge', 'tempsc_lifeboat']) if (fittedAt(state, d, p.fieldId)) keep *= 1 - (stat(eqDef(d), 'mitigates', {}).abandonment ?? 0);
  const deaths = Math.round(pob * range(state, 0.02, 0.6) * keep);
  if (deaths > 0) { bumpStat(state, 'deaths', deaths); state.company.reputation = clamp(state.company.reputation - Math.min(30, 3 + deaths / 5), 0, 100); }
  notify(state, 'accident', { incidentType: 'platform_fire', fieldId: p.fieldId, platformUid: p.uid, deaths, fatal: deaths > 0, pause: true, level: 'major',
    text: `Fire on the ${def?.name ?? 'platform'} at ${fieldDef(p.fieldId)?.name}: ${deaths ? `${deaths} dead; ` : ''}production down ${down} days.` });
  chargeSpill(state, p.fieldId, range(state, 500, 5000), 'platform fire', { pause: false });
  p.fire = { day: state.day, untilDay: state.day + int(state, 5, 30) };
}

// ---- daily ------------------------------------------------------------------------------------------
export function tickRegimes(state, dom) {
  if (!m3On(state)) return;
  const y = yf(state);
  const E = econState(state);
  // inspections
  for (const [rid, wells] of Object.entries(sitesByRegion(state))) {
    for (const reg of safetyRegimesAt(rid, y)) if (chance(state, (reg.inspectionsPerYear ?? 0) / 365)) inspect(state, rid, reg, wells);
  }
  enforceShutIns(state);
  tickOffshoreBlowouts(state);
  tickSpillCleanup(state);
  // weekly flowline spill rolls on offshore producers
  if (state.day % 7 === 0) {
    const off = {};
    for (const w of allWells(state)) if (w.offshore && w.status === 'PRODUCING' && w.prod?.rate > 0 && !w.ownerOperatorId) off[w.fieldId] = (off[w.fieldId] ?? 0) + 1;
    for (const [fid, n] of Object.entries(off)) if (chance(state, FLOWLINE_SPILL_PER_WELL_WEEK * n)) chargeSpill(state, fid, range(state, 50, 2000), 'flowline failure');
  }
  // storm-damaged platforms (ops agent sets status 'DAMAGED'): one spill roll per damage episode
  for (const p of Object.values(state.platforms ?? {})) {
    if (p.status === 'DAMAGED' && E.platformSpill?.[p.uid] == null) {
      (E.platformSpill ??= {})[p.uid] = state.day;
      if (chance(state, 0.5)) chargeSpill(state, p.fieldId, range(state, 200, 10000), 'storm damage');
    } else if (p.status !== 'DAMAGED' && E.platformSpill?.[p.uid] != null) delete E.platformSpill[p.uid];
    if (p.status === 'INSTALLED' && chance(state, platformFireChance(state, p))) platformFire(state, p);
  }
  if (dom === 1) {
    monthlyInsurance(state);
    if (ownsOsro(state)) spend(state, eraDollars(state, stat(eqDef('spill_response_contract'), 'annualFee', 500000), 1990) / 12, 'insurance', false);
  }
}

export function regimeView(state, regionId) {
  const y = yf(state);
  const fields = Object.keys(state.fields).filter((fid) => fieldDef(fid)?.regionId === regionId);
  return {
    regionId, spill: spillRegime(y), remoteMult: SPILL_REMOTE_MULT[regionId] ?? 1, international: !isUsRegion(regionId),
    safety: safetyRegimesAt(regionId, y).map((r) => ({ ...r })), incidentMult: regimeIncidentMult(state, regionId, { offshore: true }),
    insurance: { mult: offshoreInsuranceMult(y), baseRate: BASE_OFFSHORE_INSURANCE_RATE, insuredValue: offshoreInsuredValue(state), premiumPerYear: offshoreInsuredValue(state) * BASE_OFFSHORE_INSURANCE_RATE * offshoreInsuranceMult(y) },
    opa90: y >= OPA_FROM && OPA_REGIONS.includes(regionId) ? { osro: ownsOsro(state) } : null,
    safetyCases: fields.map((fid) => ({ fieldId: fid, ...(safetyCaseStatus(state, fid) ?? {}) })).filter((s) => s.regime),
    shutIns: fields.filter((fid) => state.m3econ?.shutIns?.[fid]).map((fid) => ({ fieldId: fid, reason: state.m3econ.shutIns[fid] })),
    from: M3_FROM,
  };
}
